import Image from "next/image";

import { FC, useState } from "react";

import Heading from "../Heading";

import Box, { BoxProps } from "@mui/material/Box";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import { useMediaQuery } from "@mui/material";
import Fade from "@mui/material/Fade";

import styles from "./styles";

interface Tab {
  id: number;
  label: string;
  image: string;
  content: string;
}

const TABS: Tab[] = [
  {
    id: 0,
    label: "Digital twins of your products",
    image: `${process.env.NEXT_PUBLIC_CLOUDFRONT_URL}/pages/nfcTagsPage/tabs/0.png`,
    content:
      "Harness the potential of blockchain technology, permanently lining physical items to its digital ones to give new possibilities of ownership and authentications of products. Take your product to the next level and offer your customers even more personalized and authenticated products by combining physical and digital assets.",
  },
  {
    id: 1,
    label: "Powering Your Brand's Sale",
    image: `${process.env.NEXT_PUBLIC_CLOUDFRONT_URL}/pages/nfcTagsPage/tabs/1.png`,
    content:
      "The digital world is rushing forward. This is the perfect moment to enter the new digital dimension and become a recognizable brand in the web3 world, a new space where recognition can only serve as an advantage. Implement our technology, increase revenue and watch the growing interest in your products in the market.",
  },
  {
    id: 2,
    label: "Augmented customer experience",
    image: `${process.env.NEXT_PUBLIC_CLOUDFRONT_URL}/pages/nfcTagsPage/tabs/2.png`,
    content:
      "Now customers buying your products can easily, with a smartphone, scan an NFC tag attached to your products and discover a new digital layer augmenting their experience.  It allows you to build whole new seller-buyer relationships.",
  },
  {
    id: 3,
    label: "Clothes ready for 2030 world",
    image: `${process.env.NEXT_PUBLIC_CLOUDFRONT_URL}/pages/nfcTagsPage/tabs/3.png`,
    content:
      "According to an EU directive, clothing must be durable and recyclable by 2030, and its production must respect humans and the planet. Fast fashion will become a thing of the past, manufacturers will face great responsibility for what they produce, and the average useful life of a product will be significantly extended. We put all the information required by the directive on the label in digital form.",
  },
];

interface TabsSectionProps {
  boxProps?: BoxProps;
}

const TabsSection: FC<TabsSectionProps> = ({ boxProps }) => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("lg"));

  const [animate, setAnimate] = useState(true);

  const [temporaryActiveTabId, setTemporaryActiveTabId] = useState(TABS[0].id);
  const [activeTabId, setActiveTabId] = useState(TABS[0].id);

  const handleChangeActiveTabId = (id: number) => {
    setAnimate(false);
    setTemporaryActiveTabId(id);
  };

  const handleAnimationEnd = () => {
    setAnimate(true);
    setActiveTabId(temporaryActiveTabId);
  };

  return (
    <Box component="section" display="grid" {...boxProps}>
      <Heading
        heading="Why?"
        subheading={
          <>
            Extend user experience and create new revenue streams.
            <br />
            We help brands build trust within their communities and ensure the
            highest level of security for the products. With the ability to
            easily scan NFC tags with a smartphone, your customers can discover
            the digital version of your product and enjoy a truly unique
            experience.
          </>
        }
      />

      <Box
        display="grid"
        sx={{
          gridTemplateColumns: { lg: "repeat(2, 1fr)" },
          gap: 4,
          minHeight: 530,
        }}
      >
        <Box display="grid" sx={{ gap: { xs: 4, lg: 0 } }}>
          {TABS.map((tab) => (
            <Accordion
              key={tab.id}
              expanded={!isDesktop && temporaryActiveTabId === tab.id}
              sx={styles.accordion}
              onChange={() => {
                handleChangeActiveTabId(tab.id);
              }}
              disableGutters
            >
              <AccordionSummary sx={{ p: 0 }}>
                <Box
                  sx={(theme) =>
                    styles.bar(theme, {
                      isActive: temporaryActiveTabId === tab.id,
                    })
                  }
                />
                <Typography
                  component="h4"
                  variant="h1"
                  fontWeight={600}
                  lineHeight={1.1}
                  color="secondary.main"
                  sx={(theme) =>
                    styles.title(theme, {
                      isActive: temporaryActiveTabId === tab.id,
                    })
                  }
                >
                  {tab.label}
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 0, display: { lg: "none" } }}>
                <Box width="100%">
                  <Typography
                    variant="h3"
                    fontSize={16}
                    fontWeight={400}
                    color="grey.400"
                  >
                    {tab.content}
                  </Typography>
                </Box>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
        <Fade
          in={animate}
          appear={false}
          onExited={handleAnimationEnd}
          unmountOnExit={false}
        >
          <Box sx={{ display: { xs: "none", lg: "grid" } }}>
            <Image
              key={activeTabId}
              src={TABS[activeTabId].image}
              alt=""
              width={350}
              height={350}
              style={{ objectFit: "cover", margin: "0 auto" }}
            />
            <Typography
              variant="h4"
              fontWeight={400}
              lineHeight={1.4}
              textAlign="justify"
            >
              {TABS[activeTabId].content}
            </Typography>
          </Box>
        </Fade>
      </Box>
    </Box>
  );
};

export default TabsSection;
